// Lon/lat -> MGA (Map Grid of Australia) easting/northing, computed client-side.
//
// Why client-side: the coordinate readout updates on every mouse move. Round-tripping
// that to /api/util would be absurd, and the only projection dependency the frontend
// would otherwise need is proj4 (~40 kB) for maths that fits in this file.
//
// MGA zones are just UTM zones on the GDA2020 datum (GRS80 ellipsoid). The transverse
// Mercator series below is the standard Snyder/USGS expansion, accurate to ~1 mm within
// a zone - far beyond what a cursor readout needs. It is cross-checked against pyproj
// (EPSG:7850 / EPSG:7854), the same library the backend uses, by
// backend/scripts/check_mga.py.
//
// Datum note: Cesium reports WGS84 lon/lat. WGS84 and GDA2020 agree to within a few
// centimetres at the current epoch, so projecting Cesium's output directly gives GDA2020
// MGA. Do NOT feed GDA94 coordinates through here expecting GDA2020 out - the datums are
// ~1.8 m apart, which is exactly the drift CLAUDE.md warns about.

/** MGA zones in use: 50 = Pilbara (Mindy, Nullagine, Wyloo), 54 = SOE Kangwinan. */
export type MgaZone = 50 | 54;

export interface MgaCoord {
  zone: MgaZone;
  easting: number;
  northing: number;
}

export const MGA_ZONES: { zone: MgaZone; label: string; epsg: number }[] = [
  { zone: 50, label: "MGA2020 Zone 50", epsg: 7850 },
  { zone: 54, label: "MGA2020 Zone 54", epsg: 7854 },
];

// GRS80 - the ellipsoid for both GDA94 and GDA2020 (they differ by datum realisation,
// not by ellipsoid).
const A = 6378137.0;
const F = 1 / 298.257222101;
const E2 = 2 * F - F * F; // first eccentricity squared
const EP2 = E2 / (1 - E2); // second eccentricity squared
const K0 = 0.9996; // UTM scale factor on the central meridian
const FALSE_EASTING = 500000.0;
const FALSE_NORTHING = 10000000.0; // southern hemisphere

const DEG = Math.PI / 180;

/** Central meridian of an MGA/UTM zone, in degrees east. Zone 50 -> 117, zone 54 -> 141. */
export function centralMeridian(zone: MgaZone): number {
  return zone * 6 - 183;
}

/** Projected EPSG for a zone (GDA2020 / MGA): 50 -> 7850, 54 -> 7854. */
export function epsgForZone(zone: MgaZone): number {
  return MGA_ZONES.find((z) => z.zone === zone)?.epsg ?? 7850;
}

/**
 * The supported zone whose central meridian is nearest this longitude - used only to pick a
 * project's DEFAULT zone from where its data actually sits (SOE Kangwinan near Weipa lands on
 * 54, the Pilbara projects on 50). An explicit choice in the toolbar always wins.
 *
 * Nearest-central-meridian rather than the true UTM zone formula because only 50 and 54 are
 * offered: a project whose data sat in, say, zone 51 should default to the closest thing we
 * can actually display, not fall back to an arbitrary constant.
 */
export function defaultZoneForLon(lon: number): MgaZone {
  return MGA_ZONES.reduce((best, z) =>
    Math.abs(lon - centralMeridian(z.zone)) < Math.abs(lon - centralMeridian(best.zone)) ? z : best,
  ).zone;
}

/**
 * Project geographic lon/lat (degrees, GDA2020 ~ WGS84) to MGA easting/northing for the
 * given zone. The zone is supplied by the caller, not inferred from longitude: a project
 * has one working zone and its coordinates must stay in it even when the cursor strays
 * over a zone boundary.
 */
export function toMGA(lonDeg: number, latDeg: number, zone: MgaZone): MgaCoord {
  const lat = latDeg * DEG;
  const dLon = (lonDeg - centralMeridian(zone)) * DEG;

  const sinLat = Math.sin(lat);
  const cosLat = Math.cos(lat);
  const tanLat = Math.tan(lat);

  const N = A / Math.sqrt(1 - E2 * sinLat * sinLat); // radius of curvature in the prime vertical
  const T = tanLat * tanLat;
  const C = EP2 * cosLat * cosLat;
  const AA = dLon * cosLat;

  // Meridional arc from the equator.
  const M =
    A *
    ((1 - E2 / 4 - (3 * E2 * E2) / 64 - (5 * E2 * E2 * E2) / 256) * lat -
      ((3 * E2) / 8 + (3 * E2 * E2) / 32 + (45 * E2 * E2 * E2) / 1024) * Math.sin(2 * lat) +
      ((15 * E2 * E2) / 256 + (45 * E2 * E2 * E2) / 1024) * Math.sin(4 * lat) -
      ((35 * E2 * E2 * E2) / 3072) * Math.sin(6 * lat));

  const A2 = AA * AA;
  const A3 = A2 * AA;
  const A4 = A3 * AA;
  const A5 = A4 * AA;
  const A6 = A5 * AA;

  const easting =
    FALSE_EASTING +
    K0 *
      N *
      (AA +
        ((1 - T + C) * A3) / 6 +
        ((5 - 18 * T + T * T + 72 * C - 58 * EP2) * A5) / 120);

  // In the southern hemisphere M is negative, so the false northing brings this back
  // positive - no separate hemisphere branch needed.
  const northing =
    FALSE_NORTHING +
    K0 *
      (M +
        N *
          tanLat *
          (A2 / 2 +
            ((5 - T + 9 * C + 4 * C * C) * A4) / 24 +
            ((61 - 58 * T + T * T + 600 * C - 330 * EP2) * A6) / 720));

  return { zone, easting, northing };
}

/** "462 183" - metres with thin-space grouping, no decimals. Grid coords at mm precision
 *  are noise for a cursor readout, and the spacing makes 7-digit northings readable. */
export function formatMetres(v: number): string {
  return Math.round(v)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}
